import { create, v, w } from '@dojo/framework/core/vdom';
import { icache } from '@dojo/framework/core/middleware/icache';
import { createNlsMiddleware, keymap, pathmap } from '@holmescorp/lcc-state';
import { theme } from '@dojo/framework/core/middleware/theme';
import { status, TrackedStatePlus } from '../middleware/status';
import { TopicDetail } from './interfaces';
import { Icon } from '@holmescorp/base-widgets';
import { CompletedViz } from './viz';
import { Keys } from '@holmescorp/common';
import { find, isEmpty } from 'lodash-es';
import has from '@dojo/framework/core/has';

import themeCss from '../css/cards.m.css';

export interface TopicSetCardProperties {
	path: string;
	topic: TopicDetail;
	index: number;
}

const factory = create({
	icache,
	nls: createNlsMiddleware('ditabook'),
	quizNls: createNlsMiddleware('quiz'),
	theme,
	status
}).properties<TopicSetCardProperties>();

export const TopicSetCard = factory(({ id, properties, middleware: { nls, quizNls, theme, status } }) => {
	const css = theme.classes(themeCss);
	const {
		format,
		messages: {
			quiz = '[ditabook] quiz',
			reading: reading_text = '[ditabook] reading',
			functional_area_complete = '[ditabook] functional_area_complete',
			review_materials = '[ditabook] review_materials',
			take_bonus_quiz = '[ditabook] take_bonus_quiz',
			completed = '[ditabook] completed',
			take_quiz = '[ditabook] take_quiz',
			retake_quiz = `[ditabook] retake_quiz`,
			get_started = `[ditabook] get_started`
		}
	} = nls();

	const hasBonusQuizzes = false;

	const { continue: continueText = '[quiz] continue' } = quizNls().messages;

	const { path, topic } = properties();
	const { title, key } = topic.attributes;

	const {
		complete: statusComplete,
		total: statusTotal,
		quizScore,
		quizFail,
		quizPassed,
		quizStarted,
		readingComplete,
		percent,
		percentStr,
		failIcon
	} = status.data(key);

	const retakeQuiz = quizStarted && quizFail && readingComplete;
	const takeQuiz = !quizStarted && readingComplete;
	const faComplete = quizPassed && readingComplete;
	const bonusQuiz = hasBonusQuizzes && faComplete;

	const topicgroups = topic.children.filter((k: any) => 'topicgroup' in k);

	// Calculate total differently if using Rise.
	const useRise = has('use-rise');
	let complete = statusComplete;
	let total = statusTotal;
	if (useRise) {
		total = topicgroups.length;
		let riseComplete = 0;
		topicgroups.map((k: any) => {
			const { key } = k.topicgroup.attributes;
			const state = status.data(key) ? status.data(key) : ({} as TrackedStatePlus);
			if (!isEmpty(state) && state.complete == state.total) {
				riseComplete++;
			}
		});
		complete = riseComplete;
	}

	const readNext = find(topicgroups, (k: any) => {
		const { key } = k.topicgroup.attributes;
		const state = status.data(key) ? status.data(key) : ({} as TrackedStatePlus);
		if (!isEmpty(state)) {
			return state.complete !== state.total;
		}
		return true;
	});

	const readFirst = find(topicgroups, (k, index) => {
		return index === 0;
	});

	const take = v(
		'span',
		{
			classes: [css.action],
			onclick: (event: MouseEvent) => {
				event && event.stopPropagation();
				const { path } = keymap.get(key)!;
				const { view } = pathmap.get(`${path}/quiz`)!;
				view();
			},
			onkeydown: (event: KeyboardEvent) => {
				const { path } = keymap.get(key)!;
				const { view } = pathmap.get(`${path}/quiz`)!;
				switch (event.key) {
					case Keys.Enter:
					case Keys.Space:
					case Keys.Spacebar:
						event.preventDefault();
						view();
				}
			}
		},
		[bonusQuiz ? take_bonus_quiz : takeQuiz ? take_quiz : retake_quiz]
	);

	const bonus = v(
		'span',
		{
			classes: [css.action],
			onclick: (event: MouseEvent) => {
				event && event.stopPropagation();
				const { path } = keymap.get(key)!;
				const { view } = pathmap.get(`${path}/quiz`)!;
				view();
			},
			onkeydown: (event: KeyboardEvent) => {
				const { path } = keymap.get(key)!;
				const { view } = pathmap.get(`${path}/quiz`)!;
				switch (event.key) {
					case Keys.Enter:
					case Keys.Space:
					case Keys.Spacebar:
						event.preventDefault();
						view();
				}
			}
		},
		[take_bonus_quiz]
	);

	const review = v(
		'span',
		{
			classes: [css.action],
			onclick: (event: MouseEvent) => {
				event && event.stopPropagation();
				const { key } = readFirst.topicgroup.attributes;
				const { path } = keymap.get(key)!;
				const { view } = pathmap.get(path)!;
				view();
			},
			onkeydown: (event: KeyboardEvent) => {
				const { key } = readFirst.topicgroup.attributes;
				const { path } = keymap.get(key)!;
				const { view } = pathmap.get(path)!;
				switch (event.key) {
					case Keys.Enter:
					case Keys.Space:
					case Keys.Spacebar:
						event.preventDefault();
						view();
				}
			}
		},
		[review_materials]
	);

	const cont = v(
		'span',
		{
			classes: [css.action],
			onclick: (event: MouseEvent) => {
				event && event.stopPropagation();
				const { key } = readNext.topicgroup.attributes;
				const { path } = keymap.get(key)!;
				const { view } = pathmap.get(path)!;
				view();
			},
			onkeydown: (event: KeyboardEvent) => {
				const { key } = readNext.topicgroup.attributes;
				const { path } = keymap.get(key)!;
				const { view } = pathmap.get(path)!;
				switch (event.key) {
					case Keys.Enter:
					case Keys.Space:
					case Keys.Spacebar:
						event.preventDefault();
						view();
				}
			}
		},
		[continueText]
	);

	const getStarted = v(
		'span',
		{
			classes: [css.action],
			onclick: (event: MouseEvent) => {
				event && event.stopPropagation();
				const { key } = readFirst.topicgroup.attributes;
				const { path } = keymap.get(key)!;
				const { view } = pathmap.get(path)!;
				view();
			},
			onkeydown: (event: KeyboardEvent) => {
				const { key } = readFirst.topicgroup.attributes;
				const { path } = keymap.get(key)!;
				const { view } = pathmap.get(path)!;
				switch (event.key) {
					case Keys.Enter:
					case Keys.Space:
					case Keys.Spacebar:
						event.preventDefault();
						view();
				}
			}
		},
		[get_started]
	);

	return v('card', { id: `topic-card-${path}-${id}`, tabIndex: 0, classes: [theme.variant(), css.cardRoot] }, [
		v('div', {}, [
			v('div', { classes: [css.title] }, [v('h1', {}, [title])]),
			faComplete
				? v('div', { classes: [css.faComplete] }, [
						v('div', { classes: [css.icon] }, [w(Icon, { faname: 'check-circle', size: '3x' })]),
						v('div', {}, [functional_area_complete]),
						v('h3', {}, [`${quiz}: ${quizScore}`])
				  ])
				: v('div', { classes: [css.progress] }, [
						v('div', { classes: [css.reading] }, [
							v('span', { classes: [css.label] }, [`${reading_text}: `]),
							v('span', {}, [`${format('complete_of_total', { complete, total })} ${completed}`])
						]),
						w(CompletedViz, { percent: complete / total, xqa: 'reading-viz' }),
						v('div', { classes: [css.quiz] }, [
							v('span', { classes: [css.label] }, [`${quiz}: `]),
							v('span', {}, [
								quizStarted
									? v('span', {}, [
											v('span', {}, [
												`${completed} ${format('at_score', { score: percentStr })}`
											]),
											v('span', {}, [failIcon])
									  ])
									: quizScore
							])
						]),
						w(CompletedViz, { percent, xqa: 'quiz-viz' })
				  ]),
			v('div', { classes: [css.actions] }, [
				faComplete ? review : null,
				bonusQuiz ? bonus : null,
				faComplete
					? null
					: takeQuiz
					? take
					: retakeQuiz
					? take
					: !quizStarted && complete === 0
					? getStarted
					: cont
			])
		])
	]);
});
